import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button } from 'react-bootstrap'
import useAppointmentTypesState from '../../../stores/Configuration/useAppointmentTypesState'
import TimeLength from './TimeLength'
import TimesList from './TimeGenerator/TimesList'
import { calculateTime } from '../../../utils/hoursAndMinutes'
import AddTimesModal from './TimeGenerator/AddTimesModal'
import AddSeriesModal from './TimeGenerator/AddSeriesModal'
import styled, { keyframes } from 'styled-components'
import { cloneDeep } from 'lodash'

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  display: flex;
  gap: 2rem;
  padding: 0.25rem 0;
  animation: ${fadeUp} 0.4s ease both;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`

const ControlPanel = styled.div`
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 767px) {
    width: 100%;
  }
`

const CalendarPanel = styled.div`
  flex: 1;
  min-width: 0;
`

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 14px;
  padding: 1.25rem;
`

const SectionLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ba3af;
  margin: 0 0 0.75rem;
`

const StyledButton = styled(Button)`
  width: 100%;
  margin-bottom: 0.5rem;

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`

const SaveButton = styled(Button)`
  margin-top: 0.25rem;
  margin-bottom: 1.5rem;

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eef0f3;
  margin: 0.25rem 0;
`

const TimesGeneratorForm = () => {
  const { t } = useTranslation()
  const [openAddTimesModal, setOpenAddTimesModal] = useState(false)
  const [openAddSeriesModal, setOpenAddSeriesModal] = useState(false)

  const {
    handleSubmit,
    selectedAppointmentType,
    hours,
    updateTimeSlots,
    initTimesGeneratorForm,
    error,
    success,
    resetStates,
  } = useAppointmentTypesState()

  useEffect(() => {
    initTimesGeneratorForm()
    return () => resetStates()
  }, [initTimesGeneratorForm, resetStates])

  const durationInMinutes = selectedAppointmentType.durationInMinutes

  const createAppointmentSeries = ({ timeFrom, timeTo, breakTime }) => {
    let durationInt = parseInt(durationInMinutes)
    if (durationInt <= 0) return null
    durationInt += parseInt(breakTime)

    const newTimes = cloneDeep(hours)
    let startHour = parseInt(timeFrom.substring(0, 2))
    let startMinute = parseInt(timeFrom.substring(3, 5))
    const endHour = parseInt(timeTo.substring(0, 2))
    const endMinute = parseInt(timeTo.substring(3, 5))
    let tempTimes

    while (startHour * 60 + startMinute < endHour * 60 + endMinute) {
      tempTimes = calculateTime('plus', startHour, startMinute, durationInt)
      if (tempTimes[0] < startHour) break
      newTimes[startHour].push({ hour: startHour, minute: startMinute })
      startHour = tempTimes[0]
      startMinute = tempTimes[1]
    }
    updateTimeSlots(newTimes)
  }

  return (
    <>
      <PageWrapper>
        <ControlPanel>
          <Card>
            <SectionLabel>{t('appointment:duration')}</SectionLabel>
            <TimeLength />
          </Card>

          <Card>
            <SectionLabel>{t('appointment:addSlots')}</SectionLabel>
            <StyledButton
              className='btn-extra-padding'
              onClick={() => setOpenAddTimesModal(true)}
            >
              {t('appointment:addSingleAppointment')}
            </StyledButton>
            <StyledButton
              className='btn-extra-padding'
              onClick={() => setOpenAddSeriesModal(true)}
            >
              {t('appointment:addAppointmentSeries')}
            </StyledButton>
          </Card>

          <Divider />

          <SaveButton
            className='btn-extra-padding'
            onClick={() => handleSubmit('duration')}
          >
            {t('saveButton')}
          </SaveButton>

          {error && <Alert variant='danger'>{error}</Alert>}
          {success && (
            <Alert variant='success' className='my-2'>
              {success}
            </Alert>
          )}
        </ControlPanel>

        <CalendarPanel>
          {hours && durationInMinutes ? (
            <TimesList
              durationInMinutes={durationInMinutes}
              hours={hours}
              updateTimeSlots={updateTimeSlots}
            />
          ) : null}
        </CalendarPanel>
      </PageWrapper>

      {openAddTimesModal && (
        <AddTimesModal
          duration={parseInt(durationInMinutes)}
          hoursArray={hours}
          updateTimeSlots={updateTimeSlots}
          handleClose={() => setOpenAddTimesModal(false)}
        />
      )}
      {openAddSeriesModal && (
        <AddSeriesModal
          createSeries={createAppointmentSeries}
          handleClose={() => setOpenAddSeriesModal(false)}
        />
      )}
    </>
  )
}

export default TimesGeneratorForm
